class ProductGrid extends HTMLElement {
  constructor() {
    super();
    this.currentPage = 1;
    this.isLoading = false;
    this.hasMorePages = true;
    this.observer = null;
    this.productBlocks = [];
    this.gridContainer = null;
  }

  connectedCallback() {
    this.style.display = "block";
    // Get data attributes
    this.totalPages = parseInt(this.dataset.paginationPages) || 1;
    this.collectionHandle = this.dataset.collectionHandle;
    setTimeout(() => this.initLazyLoad(), 200);
  }

  initLazyLoad() {
    this.productBlocks = Array.from(this.querySelectorAll(".grid__item"));
    this.gridContainer = this.querySelector("#product-grid");
    this.setupIntersectionObserver();
  }

  setupIntersectionObserver() {
    const options = {
      root: null,
      rootMargin: "100px",
      threshold: 0.1,
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !this.isLoading && this.hasMorePages) {
          this.loadNextPage();
        }
      });
    }, options);
    this.observeSentinel();
  }

  observeSentinel() {
    // Observe ~9 cards before the end. Clamp the index so small result sets
    // (very common after a filter) don't pass undefined to observe() and throw.
    const target = this.productBlocks[Math.max(0, this.productBlocks.length - 9)];
    if (target) this.observer.observe(target);
  }

  reinitLazyLoad() {
    // Called by facets.js after a filter/sort replaces the grid contents. The
    // old observer is left watching detached nodes (so infinite load stops), so
    // disconnect it and rebuild state from the freshly injected markup.
    // data-pagination-pages is refreshed by facets.js before this runs.
    if (this.observer) this.observer.disconnect();
    this.totalPages = parseInt(this.dataset.paginationPages) || 1;
    this.currentPage = 1;
    this.isLoading = false;
    this.hasMorePages = this.totalPages > 1;
    this.initLazyLoad();
  }

  async loadNextPage() {
    if (this.isLoading || this.currentPage >= this.totalPages) {
      this.hasMorePages = false;
      return;
    }

    this.isLoading = true;

    try {
      const nextPage = this.currentPage + 1;
      const url = new URL(window.location);
      url.searchParams.set("page", nextPage);

      // Preserve existing filters and sorting
      const currentParams = new URLSearchParams(window.location.search);
      currentParams.forEach((value, key) => {
        if (key !== "page") {
          url.searchParams.set(key, value);
        }
      });

      const response = await fetch(url.toString());

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");

      // Find the product grid in the response
      const newProducts = doc.querySelectorAll("#product-grid .grid__item");

      if (newProducts.length === 0) {
        this.hasMorePages = false;
        return;
      }

      this.observer.disconnect();

      // Append new products to the current grid
      newProducts.forEach((product) => {
        // Clone the node and append it
        const clonedProduct = product.cloneNode(true);
        this.gridContainer.appendChild(clonedProduct);
        this.productBlocks.push(clonedProduct);
      });

      // Set order css on callout element only if on depict grid
      const calloutEl = this.querySelector(".grid-customize-callout");
      const depictGridEl = this.querySelector("#product-grid.depict-grid");
      if (calloutEl && depictGridEl) {
        const numBlocks = this.productBlocks.length;
        calloutEl.style.setProperty("order", `-${numBlocks - 8}`, "important");
      }

      this.observeSentinel();

      // Update current page
      this.currentPage = nextPage;

      // Check if we've reached the last page
      if (nextPage >= this.totalPages) {
        this.hasMorePages = false;
      }

      // Trigger any necessary re-initialization for new products
      this.initializeNewProducts(newProducts);
    } catch (error) {
      console.error("Error loading next page:", error);
      this.hasMorePages = false;
    } finally {
      this.isLoading = false;
    }
  }

  initializeNewProducts(newProducts) {
    // Initialise the image sliders on the newly appended cards. swiperSlider()
    // only targets cards without the swiper-initialized class, so existing
    // sliders are left untouched.
    if (typeof swiperSlider === "function") {
      swiperSlider();
    }

    // Re-run the image height function for new products
    if (typeof setPLPImageHeight === "function") {
      setTimeout(setPLPImageHeight, 100);
    }

    // Trigger scroll animations for new products if enabled
    if (window.ScrollTrigger) {
      window.ScrollTrigger.refresh();
    }
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }
}

// Register the custom element
if (!customElements.get("product-grid")) {
  customElements.define("product-grid", ProductGrid);
}